import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { formatDateTime, formatDay } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from '../bookings/util.js';

// Office: Stock > UT stock counts. Each team's weekly count, newest week
// first (the week open now, or next to open, at the front). A closed week
// with no count is Missing. Click a count to see it in full.
export default function StockCounts() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const [open, setOpen] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    api(`/stock/counts?weeks=${weeks}`).then((d) => { setData(d); setError(null); }, (e) => setError(problem(e)));
    api('/stock/catalogue').then(setCatalogue, () => {});
  }, [weeks]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    setNotice(null);
    try {
      const r = await api('/stock/catalogue/refresh', { method: 'POST' });
      setNotice({ tone: 'ok', text: `Copied ${r.items} items from Nano Fibre.` });
      load();
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    } finally {
      setRefreshing(false);
    }
  };

  const countOf = (teamId, week) => data.counts.find((c) => c.teamId === teamId && c.week === week);
  const synced = catalogue?.items?.reduce((a, i) => (i.syncedAt > a ? i.syncedAt : a), '');

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>UT stock counts</h1>
            <p className="muted">Each team counts the UT stock on its van once a week. The count is due Friday to Monday and counts as the new week; a team can also count on any other day. Teams that haven’t counted get a reminder in Messages on Monday morning.</p>
          </div>
        </div>
        {data && (
          <p className="small">
            Counted: <strong>{data.items.map((i) => i.description).join(', ') || 'nothing yet'}</strong>.{' '}
            <span className="muted">Which items are counted is set in Nano Fibre’s SKU Manager (Nano Utilities, “NU App Stock Count”){synced ? `, last copied ${formatDateTime(synced)}` : ''}.</span>{' '}
            <button type="button" className="link-btn with-icon" onClick={refresh} disabled={refreshing}><RefreshCw size={14} aria-hidden="true" /> {refreshing ? 'Copying…' : 'Copy now'}</button>
          </p>
        )}
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
      </section>

      <section className="card">
        <div className="ticket-views">
          <label className="small">Show{' '}
            <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} aria-label="Weeks to show">
              {[4, 8, 13, 26].map((n) => <option key={n} value={n}>{n} weeks</option>)}
            </select>
          </label>
          <button type="button" className="btn icon-only" aria-label="Refresh" onClick={load}><RefreshCw size={18} aria-hidden="true" /></button>
        </div>
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!data && !error && <p className="muted">Loading…</p>}
        {data && !data.teams.length && <p className="muted">No teams yet.</p>}
        {data && data.teams.length > 0 && (
          <div className="table-scroll">
            <table className="table stock-grid">
              <thead>
                <tr>
                  <th>Team</th>
                  {data.weeks.map((w) => (
                    <th key={w.week}>
                      {w.label}
                      <div className="small muted">{w.state === 'open' ? 'Due now' : `w/c ${formatDay(w.week)}`}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.teams.map((t) => (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong><div className="small muted">{t.members || 'Nobody in the team'}</div></td>
                    {data.weeks.map((w) => {
                      const c = countOf(t.id, w.week);
                      if (c) {
                        return (
                          <td key={w.week}>
                            <button type="button" className="stock-cell" onClick={() => setOpen(c)} aria-label={`${t.name} ${w.label} count`}>
                              <ul className="stock-lines small">{c.lines.map((l) => <li key={l.itemId}><span>{l.description}</span><strong>{l.qty}</strong></li>)}</ul>
                              <span className="small muted">{c.countedByName}, {formatDateTime(c.countedAt)}</span>
                            </button>
                          </td>
                        );
                      }
                      return (
                        <td key={w.week}>
                          {w.state === 'closed' ? <span className="status status-aborted">Missing</span> : <span className="muted small">Not counted yet</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal open={Boolean(open)} title={open ? `${open.teamName}: ${open.weekLabel} UT count` : ''} onClose={() => setOpen(null)}>
        {open && (
          <div className="stack">
            <p className="small muted">Counted by {open.countedByName} on {formatDateTime(open.countedAt)}{open.receivedAt && Math.abs(new Date(open.receivedAt) - new Date(open.countedAt)) > 10 * 60_000 ? ` (reached us ${formatDateTime(open.receivedAt)})` : ''}.</p>
            <table className="table">
              <thead><tr><th>Item</th><th>SKU</th><th className="num">Count</th></tr></thead>
              <tbody>{open.lines.map((l) => <tr key={l.itemId}><td>{l.description}</td><td className="muted small">{l.sku}</td><td className="num"><strong>{l.qty}</strong></td></tr>)}</tbody>
            </table>
            {open.note && <p><strong>Note:</strong> {open.note}</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
